// pages/Analytics.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/AppIcon";
import {Breadcrumb} from "components/ui/Breadcrumb";
import { formatCurrency } from "utils/localization";
import { analyticsAPI } from "services/api";

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState("last30days");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all analytics data in parallel
        const [summaryRes, trendsRes, categoryRes, eventsRes, activityRes] =
          await Promise.all([
            analyticsAPI.getSummary(timeRange),
            analyticsAPI.getMonthlyTrends(timeRange),
            analyticsAPI.getCategoryBreakdown(timeRange),
            analyticsAPI.getTopEvents(timeRange),
            analyticsAPI.getRecentActivity(timeRange),
          ]);

        const analyticsData = {
          summary: summaryRes.data.summary,
          monthlyTrends: trendsRes.data.monthlyTrends,
          categoryBreakdown: categoryRes.data.categoryBreakdown,
          topEvents: eventsRes.data.topEvents,
          recentActivity: activityRes.data.recentActivity,
        };

        setAnalytics(analyticsData);
      } catch (err) {
        console.error("Error fetching analytics:", err);
        setError("Failed to load analytics data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [timeRange]);

  const getActivityIcon = (type) => {
    const icons = {
      expense_added: "Plus",
      settlement: "CheckCircle",
      event_created: "Calendar",
      participant_added: "UserPlus",
    };
    return icons[type] || "Activity";
  };

  const getActivityColor = (type) => {
    const colors = {
      expense_added: "text-blue-600 bg-blue-100",
      settlement: "text-green-600 bg-green-100",
      event_created: "text-purple-600 bg-purple-100",
      participant_added: "text-orange-600 bg-orange-100",
    };
    return colors[type] || "text-gray-600 bg-gray-100";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <Icon
            name="AlertCircle"
            size={48}
            className="mx-auto text-red-500 mb-4"
          />
          <h3 className="text-lg font-medium text-red-800 mb-2">
            Error Loading Analytics
          </h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-muted-foreground">No analytics data available</div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <Breadcrumb />
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
            <p className="text-muted-foreground mt-2">
              Track your spending patterns and event insights
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="last3months">Last 3 Months</option>
              <option value="lastyear">Last Year</option>
            </select>
            <Link
              to="/analytics/detailed"
              className="flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-md font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="BarChart3" size={16} />
              <span>Detailed View</span>
            </Link>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Events</p>
                <p className="text-2xl font-bold text-foreground">
                  {analytics.summary.totalEvents}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Icon name="Calendar" size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(analytics.summary.totalAmount)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Icon name="DollarSign" size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Settled</p>
                <p className="text-2xl font-bold text-green-60">
                  {formatCurrency(analytics.summary.settledAmount)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Icon name="CheckCircle" size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Events</p>
                <p className="text-2xl font-bold text-orange-600">
                  {analytics.summary.activeEvents}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Icon name="Activity" size={24} className="text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Participants</p>
                <p className="text-2xl font-bold text-purple-600">
                  {analytics.summary.totalParticipants}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Icon name="Users" size={24} className="text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Trends Chart */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Monthly Spending Trends</h3>
              <Link
                to="/analytics/trends"
                className="text-primary hover:text-primary/80 font-medium text-sm"
              >
                View Details →
              </Link>
            </div>
            <div className="space-y-4">
              {analytics.monthlyTrends.slice(-5).map((month, index) => (
                <div
                  key={month.month}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-primary">
                        {month.month}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{month.events} events</p>
                      <div className="w-32 bg-muted rounded-full h-2 mt-1">
                        <div
                          className="bg-primary h-2 rounded-full"
                          style={{ width: `${(month.amount / 2500) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <span className="font-semibold">
                    {formatCurrency(month.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Spending by Category</h3>
              <Link
                to="/analytics/categories"
                className="text-primary hover:text-primary/80 font-medium text-sm"
              >
                View Details →
              </Link>
            </div>
            <div className="space-y-4">
              {analytics.categoryBreakdown.map((category) => (
                <div key={category.category} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="font-medium text-sm">
                        {category.category}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold">
                        {formatCurrency(category.amount)}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {category.percentage}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${category.percentage}%`,
                        backgroundColor: category.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Events */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Top Events by Amount</h3>
              <Link
                to="/events"
                className="text-primary hover:text-primary/80 font-medium text-sm"
              >
                View All →
              </Link>
            </div>
            <div className="space-y-4">
              {analytics.topEvents.map((event, index) => (
                <Link
                  key={event.id}
                  to={`/event-details/${event.id}`}
                  className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">
                        #{index + 1}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{event.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.participants} participants • {event.status}
                      </p>
                    </div>
                  </div>
                  <span className="font-semibold">
                    {formatCurrency(event.amount)}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Recent Activity</h3>
              <Link
                to="/analytics/activity"
                className="text-primary hover:text-primary/80 font-medium text-sm"
              >
                View All →
              </Link>
            </div>
            <div className="space-y-4">
              {analytics.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div
                    className={`p-2 rounded-full ${getActivityColor(
                      activity.type
                    )}`}
                  >
                    <Icon name={getActivityIcon(activity.type)} size={16} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {activity.user} {activity.type.replace("_", " ")}
                      {activity.amount > 0 && (
                        <span className="font-semibold">
                          {" "}
                          {formatCurrency(activity.amount)}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activity.event} • {activity.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
